<template>
  <v-card class="rounded-xl">
    <!-- Header -->
    <div class="modern-header">
      <div class="header-content">
        <div class="header-left">
          <v-icon class="header-icon" size="28" color="white"
            >mdi-check-decagram</v-icon
          >
          <h2
            class="header-title"
            style="text-transform: uppercase; font-size: 1.1rem"
          >
            {{ modalTitle }}
          </h2>
        </div>
        <div class="header-actions">
          <v-btn icon large class="close-btn" @click="$emit('close')">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <v-container fluid class="px-4 pt-4 pb-4">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary" />
        <div class="mt-2 grey--text">Cargando circuito...</div>
      </div>

      <!-- MODO CONFIGURACION: Agregar aprobadores con drag & drop -->
      <template v-if="isConfigMode && !loading">
        <!-- Banner de Requerimientos Masivos Seleccionados -->
        <div
          v-if="isBulkMode"
          class="bulk-req-preview mb-3 pa-3 rounded-lg"
          style="background: #f1f5f9; border-left: 4px solid #6366f1;"
        >
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="font-weight-bold text-caption" style="color: #334155;">
              <v-icon x-small color="indigo" class="mr-1">mdi-format-list-checks</v-icon>
              Requerimientos que recibirán este circuito de aprobación ({{ bulkRequirements.length }})
            </span>
          </div>
          <div class="d-flex flex-wrap" style="gap: 6px; max-height: 85px; overflow-y: auto;">
            <v-chip
              v-for="r in bulkRequirements"
              :key="r.ReqID"
              x-small
              color="indigo"
              outlined
              class="font-weight-medium bg-white"
            >
              #{{ r.ReqID }} - {{ r.PstName }} ({{ r.ReqQuantity }} vac.)
            </v-chip>
          </div>
        </div>

        <div class="text-caption grey--text mb-2">
          <v-icon x-small>mdi-drag</v-icon> Arrastre para reordenar los niveles
        </div>
        <v-list dense class="elevation-1 mb-2 pa-0">
          <draggable
            v-model="approvalLevels"
            @end="updateOrder"
            :move="onMove"
            handle=".drag-handle"
          >
            <v-list-item
              v-for="(row, index) in approvalLevels"
              :key="row._id"
              class="drag-item"
              style="border-bottom: 1px solid #eee"
            >
              <v-list-item-avatar
                size="36"
                color="primary"
                :class="isLastLevel(index) ? 'drag-disabled' : 'drag-handle'"
                :style="
                  isLastLevel(index)
                    ? 'cursor: not-allowed; display: flex; align-items: center; justify-content: center;'
                    : 'cursor: grab; display: flex; align-items: center; justify-content: center;'
                "
              >
                <span class="white--text font-weight-bold">{{
                  index + 1
                }}</span>
              </v-list-item-avatar>
              <v-list-item-content class="py-1">
                <s-select-position
                  v-if="!isLastLevel(index)"
                  v-model="row.ApprovalPstID"
                  :text="row.PstName"
                  clearable
                />

                <v-text-field
                  v-else
                  :value="row.PstName"
                  outlined
                  dense
                  disabled
                  hide-details
                  append-icon="mdi-menu-down"
                />
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  v-if="!isLastLevel(index)"
                  icon
                  small
                  color="error"
                  @click="removeLevel(index)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </draggable>
          <v-list-item v-if="approvalLevels.length === 0">
            <v-list-item-content class="text-center grey--text py-3">
              Defina primero los cargos de adjudicador de sueldo y Gerente RRHH
              antes de agregar mas niveles.
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <div class="d-flex justify-space-between mt-4">
          <v-btn
            :disabled="approvalLevels.length === 0"
            small
            color="info"
            @click="addLevel"
          >
            <v-icon left small>mdi-plus</v-icon>
            Agregar nivel
          </v-btn>
          <v-btn
            small
            :color="isBulkMode ? 'indigo' : 'primary'"
            class="white--text"
            @click="saveAndSend"
            :disabled="approvalLevels.length === 0"
            :loading="saving"
          >
            <v-icon left small>{{ isBulkMode ? 'mdi-send-check' : 'mdi-send' }}</v-icon>
            {{ isBulkMode ? `Guardar y Enviar a Todos (${bulkRequirements.length})` : 'Guardar y Enviar' }}
          </v-btn>
        </div>
      </template>

      <!-- MODO APROBACION: Ver estado y aprobar/rechazar -->
      <template v-if="!isConfigMode && !loading">
        <v-data-table
          dense
          :items="approvalList"
          :headers="headersApproval"
          hide-default-footer
          item-key="RapID"
        >
          <template v-slot:[`item.RapStatusName`]="{ item }">
            <v-chip x-small :color="getStatusColor(item.RapStatus)">
              {{ item.RapStatusName || getStatusText(item.RapStatus) }}
            </v-chip>
          </template>
          <template v-slot:[`item.RapApprovalDT`]="{ item }">
            {{
              item.RapApprovalDT
                ? $fun.formatDateTimeView(item.RapApprovalDT)
                : ""
            }}
          </template>
        </v-data-table>

        <!-- Panel de aprobacion para el usuario actual -->
        <template v-if="canApprove">
          <v-divider class="my-3"></v-divider>
          <v-card outlined class="pa-3">
            <div class="text-subtitle-2 mb-2">
              Nivel {{ currentPendingLevel.RapApprovalOrd }} - Su turno de
              aprobar
            </div>

            <!-- Campos editables por el validador de presupuesto -->
            <template v-if="canViewBudgetField">
              <v-row class="mb-2">
                <v-col cols="12" md="6">
                  <s-select-area
                    v-model="cceID"
                    :text="cceName"
                    centercost
                    clearable
                    :disabled="!isValBudgetPerson"
                    label="Centro de Costo"
                  />
                </v-col>
                <!-- <v-col cols="12" md="6">
                  <s-select-business-line :cceID="cceID || 0" v-model="bslID" :text="bslName"
                    :disabled="!isValBudgetPerson" label="Línea de Negocio" />
                </v-col> -->
              </v-row>

              <v-row class="mb-2">
                <v-col cols="12" md="6">
                  <s-select-definition
                    :def="1060"
                    label="Planilla"
                    v-model="typePayroll"
                    :disabled="!isValBudgetPerson"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <s-select-definition
                    :def="1481"
                    label="Régimen Laboral"
                    v-model="typeLaborRegime"
                    :disabled="!isValBudgetPerson"
                  />
                </v-col>
              </v-row>

              <v-row class="mb-2">
                <v-col cols="12">
                  <s-select
                    v-model="reqPaymentType"
                    :items="paymentTypeOptions"
                    item-text="label"
                    item-value="value"
                    label="Tipo de Compensación"
                    outlined
                    dense
                    clearable
                    :disabled="!isValBudgetPerson"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-row class="mb-2" v-if="reqPaymentType">
                <v-col
                  cols="12"
                  :md="
                    Number(reqPaymentType) === 3
                      ? 4
                      : [2].includes(Number(reqPaymentType))
                      ? 6
                      : 12
                  "
                >
                  <s-text
                    v-model="payValue1"
                    :label="payValue1Label"
                    decimal
                    prefix="S/"
                    :disabled="!isValBudgetPerson"
                    hide-details
                  />
                </v-col>

                <v-col
                  cols="12"
                  :md="Number(reqPaymentType) === 3 ? 4 : 6"
                  v-if="[2, 3].includes(Number(reqPaymentType))"
                >
                  <s-text
                    v-model="payValue2"
                    :label="payValue2Label"
                    decimal
                    prefix="S/"
                    :disabled="!isValBudgetPerson"
                    hide-details
                  />
                </v-col>

                <v-col cols="12" md="4" v-if="Number(reqPaymentType) === 3">
                  <s-text
                    v-model="payValue3"
                    label="Nivel C"
                    decimal
                    prefix="S/"
                    :disabled="!isValBudgetPerson"
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>

            <v-textarea
              v-model="observation"
              label="Observación (opcional)"
              outlined
              dense
              rows="2"
              hide-details
              class="mb-3"
            ></v-textarea>
            <div class="d-flex justify-end">
              <v-btn
                small
                color="error"
                class="mr-2"
                @click="reject"
                :loading="processing"
              >
                <v-icon left small>mdi-close-circle</v-icon>
                Rechazar
              </v-btn>
              <v-btn
                small
                color="success"
                @click="approve"
                :loading="processing"
              >
                <v-icon left small>mdi-check-circle</v-icon>
                Aprobar
              </v-btn>
            </div>
          </v-card>
        </template>
      </template>
    </v-container>
  </v-card>
</template>

<script>
import draggable from "vuedraggable";
import SSelectPosition from "@/components/Utils/sSelectPosition.vue";
import SSelectArea from "@/components/Utils/SSelectArea.vue";
import SSelectBusinessLine from "@/components/Utils/SSelectBusinessLine.vue";
import _sApproval from "@/services/HumanResource/RecruitmentSelection/HmnRequirementApprovalService.js";
import _sStaffingRequirement from "@/services/HumanResource/RecruitmentSelection/HmnStaffingRequirement.js";
import _sGenParam from "@/services/General/ParameterService";
import _sAuth from "@/services/AuthService";

let _uid = 0;

export default {
  name: "HmnApprovalCircuit",
  components: { draggable, SSelectPosition, SSelectArea, SSelectBusinessLine },
  props: {
    reqID: { type: Number, required: true },
    reqPstID: { type: Number, default: 0 },
    reqStatus: { type: Number, default: 1 },
    bulkRequirements: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      routeSecurity: null,
      // Modo configuracion
      approvalLevels: [],
      saving: false,
      // Modo aprobacion
      approvalList: [],
      observation: "",
      processing: false,
      // Compensación salarial
      valBudgetPstID: 0,
      typePayroll: null,
      typeLaborRegime: null,
      reqPaymentType: null,
      payValue1: null,
      payValue2: null,
      payValue3: null,
      cceID: null,
      bslID: null,
      bslName: "",
      cceName: "",
      paymentTypeOptions: [
        { value: 1, label: "Monto Fijo" },
        { value: 2, label: "Rango (mín - máx)" },
        { value: 3, label: "Por Niveles (A / B / C)" },
      ],
      // Headers para tabla de aprobacion
      headersApproval: [
        { text: "Orden", value: "RapApprovalOrd", width: 80, align: "center" },
        { text: "Cargo", value: "PstName" },
        { text: "Estado", value: "RapStatusName", width: 120, align: "center" },
        { text: "Aprobador", value: "PrsName", width: 150 },
        { text: "Fecha", value: "RapApprovalDT", width: 150 },
        { text: "Observación", value: "RapObservation" },
      ],
    };
  },
  computed: {
    isBulkMode() {
      return this.bulkRequirements && this.bulkRequirements.length > 0;
    },
    isConfigMode() {
      return this.reqStatus === 1 || this.isBulkMode;
    },
    modalTitle() {
      if (this.isBulkMode) {
        return `Configurar Circuito (${this.bulkRequirements.length} Requerimientos)`;
      }
      return this.isConfigMode
        ? "Configurar Circuito de Aprobación"
        : "Circuito de Aprobación";
    },
    isAdmin() {
      if (this.routeSecurity && this.routeSecurity.IsLevelAdmin !== undefined) {
        return !!this.routeSecurity.IsLevelAdmin;
      }
      return this.$fun.isAdmin();
    },
    userPstID() {
      const info = this.$fun.getUserInfo();
      return info && info.CtrContract ? info.CtrContract.PstID : 0;
    },
    isValBudgetPerson() {
      const budgetPositions = Array.isArray(this.valBudgetPstID)
        ? this.valBudgetPstID.map((id) => Number(id))
        : [];

      return budgetPositions.includes(Number(this.userPstID));
    },
    canViewBudgetField() {
      return this.isValBudgetPerson || this.isAdmin;
    },
    payValue1Label() {
      if (this.reqPaymentType === 1) return "Monto Fijo";
      if (this.reqPaymentType === 2) return "M\u00ednimo";
      if (this.reqPaymentType === 3) return "Nivel A";
      return "Valor";
    },
    payValue2Label() {
      if (this.reqPaymentType === 2) return "M\u00e1ximo";
      if (this.reqPaymentType === 3) return "Nivel B";
      return "Valor 2";
    },
    currentPendingLevel() {
      return this.approvalList.find((a) => a.RapStatus === 2) || null;
    },
    canApprove() {
      if (!this.currentPendingLevel) return false;
      return this.currentPendingLevel.ApprovalPstID === this.userPstID;
    },
  },
  methods: {
    loadRouteSecurity() {
      const routeSecurity = this.$route?.params?.security;
      if (routeSecurity && routeSecurity.UsoID != null) {
        this.routeSecurity = routeSecurity;
        return;
      }

      _sAuth
        .userOption(this.$fun.getUserID(), "HMN_STAFFING_REQUIREMENT")
        .then((r) => {
          if (r && r.data) {
            this.routeSecurity = r.data;
          }
        })
        .catch(() => {
          this.routeSecurity = null;
        });
    },
    // --- MODO CONFIGURACION ---
    loadCircuit() {
      this.loading = true;
      _sApproval
        .getCircuit(this.reqID, this.reqPstID, this.$fun.getUserID())
        .then((r) => {
          const data = r.data || [];
          console.log("circuit data", data);

          this.approvalLevels = data.map((item) => ({
            _id: ++_uid,
            ApprovalPstID: item.ApprovalPstID,
            PstName: item.PstName || "",
          }));
        })
        .catch(() => {
          // Si falla, dejar vacio para que el usuario agregue manualmente
          this.approvalLevels = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    addLevel() {
      const newLevel = {
        _id: ++_uid,
        ApprovalPstID: null,
        PstName: "",
      };

      // Mantener fijos los 2 últimos niveles al agregar nuevos.
      const lockedCount = Math.min(2, this.approvalLevels.length);
      const insertIndex = this.approvalLevels.length - lockedCount;
      this.approvalLevels.splice(insertIndex, 0, newLevel);
    },
    removeLevel(index) {
      this.approvalLevels.splice(index, 1);
    },
    isLastLevel(index) {
      return (
        index === this.approvalLevels.length - 1 ||
        index === this.approvalLevels.length - 2
      );
    },
    onMove(evt) {
      const lastIndex = this.approvalLevels.length - 1;
      const secondLastIndex = this.approvalLevels.length - 2;
      const draggedIndex = evt.draggedContext?.index;
      const relatedIndex = evt.relatedContext?.index;

      // Bloquear mover últimos 2 niveles ni colocar elementos después de ellos
      if (draggedIndex === lastIndex || draggedIndex === secondLastIndex)
        return false;
      if (
        (relatedIndex === lastIndex || relatedIndex === secondLastIndex) &&
        evt.willInsertAfter
      )
        return false;
      return true;
    },
    updateOrder() {
      // vuedraggable ya actualiza el array, solo forzamos re-render
      this.approvalLevels = [...this.approvalLevels];
    },
    onPositionChange(item, row) {
  if (item) {
    console.log("position changed", item);
    
    // Validamos si viene como Array o como Objeto directo
    const selectedItem = Array.isArray(item) ? item[0] : item;
    row.PstName = selectedItem?.GenDescription || "";
  } else {
    row.PstName = "";
  }
},
    saveAndSend() {
      const invalid = this.approvalLevels.find((l) => !l.ApprovalPstID);
      if (invalid) {
        this.$fun.alert("Seleccione un cargo en todos los niveles", "warning");
        return;
      }

      const pstIds = this.approvalLevels.map((l) => l.ApprovalPstID);
      const hasDuplicates = pstIds.length !== new Set(pstIds).size;
      if (hasDuplicates) {
        this.$fun.alert("No puede repetir cargos en el circuito", "warning");
        return;
      }

      const confirmMessage = this.isBulkMode
        ? `¿Guardar y enviar este circuito de aprobación a los ${this.bulkRequirements.length} requerimientos seleccionados?`
        : "¿Enviar este requerimiento a aprobación?";

      this.$fun
        .alert(confirmMessage, "question")
        .then((result) => {
          if (!result?.isConfirmed) return;
          this.saving = true;

          // Preparar batch con orden actualizado
          const batch = this.approvalLevels.map((level, index) => ({
            ApprovalPstID: level.ApprovalPstID,
            RapApprovalOrd: index + 1,
            RapStatus: 2,
            SecStatus: 1,
            UsrCreateID: this.$fun.getUserID(),
          }));

          const promise = this.isBulkMode
            ? _sApproval.saveCircuitMulti(
                {
                  ReqIDs: this.bulkRequirements.map((r) => r.ReqID),
                  Approvals: batch,
                },
                this.$fun.getUserID(),
              )
            : _sApproval.saveBatch(batch, this.reqID, this.$fun.getUserID());

          promise
            .then(() => {
              const successMsg = this.isBulkMode
                ? `${this.bulkRequirements.length} requerimientos enviados al circuito de aprobación exitosamente`
                : "Circuito de aprobación guardado y enviado";
              this.$fun.alert(successMsg, "success");
              this.$emit("saved");
              this.$emit("close");
            })
            .catch((e) => {
              this.$fun.alert(
                e.response?.data?.Message || "Error al guardar el circuito",
                "error",
              );
            })
            .finally(() => {
              this.saving = false;
            });
        });
    },

    // --- MODO APROBACION ---
    loadApprovals() {
      this.loading = true;
      Promise.all([
        _sApproval.byReqID(this.reqID, this.$fun.getUserID()),
        _sGenParam.search(
          { PrmName: "ValBudgetPstID_RRHH" },
          this.$fun.getUserID(),
        ),
        _sStaffingRequirement.byID(this.reqID, this.$fun.getUserID()),
      ])
        .then((results) => {
          this.approvalList = results[0].data || [];
          const budgetParam = results[1].data || {};
          this.valBudgetPstID = String(budgetParam.PrmValue || "")
            .split(",")
            .map((id) => parseInt(id.trim(), 10))
            .filter((id) => !Number.isNaN(id));

          const requirementData = results[2].data || {};
          this.reqPaymentType = requirementData.ReqPaymentType || null;
          this.payValue1 = this.getPayDisplayValue(
            requirementData.ReqPayValue1,
          );
          this.payValue2 = this.getPayDisplayValue(
            requirementData.ReqPayValue2,
          );
          this.payValue3 = this.getPayDisplayValue(
            requirementData.ReqPayValue3,
          );
          this.typePayroll = requirementData.TypePayroll || null;
          this.typeLaborRegime = requirementData.TypeLaborRegime || null;
          this.cceID = requirementData.CceID || null;
          this.cceName = requirementData.CceName || "";
          // this.bslID = requirementData.BslID || null;
          // this.bslName = requirementData.BslName || "";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getPayDisplayValue(value) {
      if (value === null || value === undefined || value === "") return null;
      const n = Number(value);
      if (!Number.isNaN(n) && String(value).trim() !== "") return n;
      try {
        const d = this.$fun.decrypt(value);
        const p = Number(d);
        return Number.isNaN(p) ? null : p;
      } catch {
        return null;
      }
    },
    encryptPayValue(value) {
      if (value === null || value === undefined || value === "") return null;
      const n = this.getPayDisplayValue(value);
      if (n === null || Number.isNaN(Number(n)) || Number(n) <= 0) return null;
      return this.$fun.encrypt(String(n));
    },
    approve() {
      if (this.isValBudgetPerson) {
        if (!this.reqPaymentType) {
          this.$fun.alert(
            "Debe seleccionar el tipo de compensaci\u00f3n antes de aprobar",
            "warning",
          );
          return;
        }
        if (!this.payValue1 || Number(this.payValue1) <= 0) {
          this.$fun.alert(
            "Debe ingresar el primer valor de compensaci\u00f3n",
            "warning",
          );
          return;
        }
        if (
          [2, 3].includes(Number(this.reqPaymentType)) &&
          (!this.payValue2 || Number(this.payValue2) <= 0)
        ) {
          this.$fun.alert(
            "Debe ingresar el segundo valor de compensaci\u00f3n",
            "warning",
          );
          return;
        }
        if (
          Number(this.reqPaymentType) === 3 &&
          (!this.payValue3 || Number(this.payValue3) <= 0)
        ) {
          this.$fun.alert(
            "Debe ingresar el Nivel C de compensaci\u00f3n",
            "warning",
          );
          return;
        }
        if (
          Number(this.reqPaymentType) === 2 &&
          Number(this.payValue2) < Number(this.payValue1)
        ) {
          this.$fun.alert(
            "El valor m\u00e1ximo no puede ser menor al m\u00ednimo",
            "warning",
          );
          return;
        }
        if (!this.typePayroll) {
          this.$fun.alert(
            "Debe seleccionar tipo de planilla antes de aprobar",
            "warning",
          );
          return;
        }
        if (!this.typeLaborRegime) {
          this.$fun.alert(
            "Debe seleccionar r\u00e9gimen laboral antes de aprobar",
            "warning",
          );
          return;
        }
      }
      this.$fun
        .alert("\u00bfAprobar este nivel?", "question")
        .then((result) => {
          if (!result?.isConfirmed) return;
          this.processing = true;
          _sApproval
            .approve(
              {
                RapID: this.currentPendingLevel.RapID,
                RapStatus: 4,
                RapObservation: this.observation,
                ReqPaymentType: this.isValBudgetPerson
                  ? this.reqPaymentType
                  : null,
                ReqPayValue1: this.isValBudgetPerson
                  ? this.encryptPayValue(this.payValue1)
                  : null,
                ReqPayValue2: this.isValBudgetPerson
                  ? this.encryptPayValue(this.payValue2)
                  : null,
                ReqPayValue3: this.isValBudgetPerson
                  ? this.encryptPayValue(this.payValue3)
                  : null,
                TypePayroll: this.isValBudgetPerson ? this.typePayroll : null,
                TypeLaborRegime: this.isValBudgetPerson
                  ? this.typeLaborRegime
                  : null,
                CceID: this.isValBudgetPerson ? this.cceID : null,
                // BslID: this.isValBudgetPerson ? this.bslID : null,
                ReqID: this.reqID,
              },
              this.$fun.getUserID(),
            )
            .then(() => {
              this.$fun.alert("Aprobado correctamente", "success");
              this.observation = "";
              this.$emit("saved");
              this.loadApprovals();
            })
            .catch((e) => {
              this.$fun.alert(
                e.response?.data?.Message || "Error al aprobar",
                "error",
              );
            })
            .finally(() => {
              this.processing = false;
            });
        });
    },
    reject() {
      if (!this.observation || this.observation.trim() === "") {
        this.$fun.alert(
          "Debe ingresar una observación para rechazar",
          "warning",
        );
        return;
      }
      this.$fun
        .alert("¿Rechazar este requerimiento?", "question")
        .then((result) => {
          if (!result?.isConfirmed) return;
          this.processing = true;
          _sApproval
            .approve(
              {
                RapID: this.currentPendingLevel.RapID,
                RapStatus: 3,
                RapObservation: this.observation,
              },
              this.$fun.getUserID(),
            )
            .then(() => {
              this.$fun.alert("Requerimiento rechazado", "success");
              this.observation = "";
              this.$emit("saved");
              this.$emit("close");
            })
            .catch((e) => {
              this.$fun.alert(
                e.response?.data?.Message || "Error al rechazar",
                "error",
              );
            })
            .finally(() => {
              this.processing = false;
            });
        });
    },
    getStatusColor(status) {
      switch (status) {
        case 1:
          return "grey";
        case 2:
          return "";
        case 3:
          return "error";
        case 4:
          return "success";
        default:
          return "";
      }
    },
    getStatusText(status) {
      switch (status) {
        case 1:
          return "No asignado";
        case 2:
          return "Pendiente";
        case 3:
          return "Rechazado";
        case 4:
          return "Aprobado";
        default:
          return "";
      }
    },
  },
  created() {
    this.loadRouteSecurity();
    if (this.isConfigMode) {
      this.loadCircuit();
    } else {
      this.loadApprovals();
    }
  },
};
</script>

<style scoped>
.modern-header {
  background: linear-gradient(135deg, #04abc5 0%, #04abc5 100%);
  padding: 8px;
  color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  padding: 8px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.header-title {
  flex: 1;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  width: 100%;
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 1 1 0%;
}

.drag-item:hover {
  background-color: #f5f5f5;
}

.drag-handle:active {
  cursor: grabbing;
}

.sortable-ghost {
  opacity: 0.4;
  background-color: #e3f2fd;
}
</style>
